<template>
    <div>
        <div v-if='!selectedHuman'>
            <div class='humans-header'>
                <p class='intro-text'>顔認証で同一と判定された人物の一覧です。名前をつけると判別がしやすくなります。</p>
                <button class='button is-success is-outlined' :disabled='!humans.length || downloadingCsv' @click='downloadCsv'>
                    <span v-if='downloadingCsv'>ダウンロード中…</span>
                    <span v-else>CSV ダウンロード</span>
                </button>
            </div>
            <p v-if='loading' class='loading-text'>読み込み中...</p>
            <p v-else-if='!humans.length' class='loading-text'>顔認証が完了している人物がいません。「写真アップロード」タブで顔認証を開始してください。</p>
            <div class='humans'>
                <div v-for='human in humans' :key='human.id' class='human-card'>
                    <div class='face-thumb' @click='openHuman(human)' v-bind:style='thumbStyle(human)'>
                        <span class='human-id'>ID: {{ human.id }}</span>
                        <span v-if='!human.representative' class='no-face'>顔画像なし</span>
                    </div>
                    <p class='face-count'>{{ human.faceCount }}枚の顔</p>
                    <div class='name-row'>
                        <input class='input is-small' type='text' v-model='human.name' placeholder='名前を入力' @keydown.enter='onNameEnter($event, human)'>
                        <button class='button is-small is-info' @click='saveName(human)'>保存</button>
                    </div>
                </div>
            </div>
        </div>

        <div v-else>
            <div class='detail-header'>
                <button class='button is-small' @click='back'>← 一覧へ戻る</button>
                <h2 class='title is-5 detail-title'>{{ selectedHuman.name || '名前未設定' }} の写真</h2>
            </div>
            <p v-if='loadingPhotos' class='loading-text'>読み込み中...</p>
            <p v-else-if='!humanPhotos.length' class='loading-text'>この人物の写真が見つかりませんでした。</p>
            <div v-else class='photos'>
                <div v-for='photo in humanPhotos' :key='photo.id' class='photo-wrap'>
                    <img v-if='photo.reduction_s3_key' :src='`${photoBaseUrl}/${photo.reduction_s3_key}`' decoding='async'>
                    <img v-else :src='`${photoBaseUrl}/${photo.s3_key}`' decoding='async'>
                    <p class='photo-name'>{{ photo.name }}</p>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import fileDownload from 'js-file-download'

export default {
    name: 'Humans',
    props: ['selectEvent'],
    data () {
        return {
            photoBaseUrl: process.env.PHOTO_BASE_URL,
            humans: [],
            loading: false,
            selectedHuman: null,
            humanPhotos: [],
            loadingPhotos: false,
            downloadingCsv: false,
            humanRequestId: 0,
            getViewPhotosHandler: null
        }
    },
    mounted () {
        const self = this
        this.getViewPhotosHandler = () => {
            self.selectedHuman = null
            self.humanPhotos = []
            self.fetchHumans()
        }
        this.$root.$on('getViewPhotos', this.getViewPhotosHandler)
        this.fetchHumans()
    },
    beforeDestroy () {
        if (this.getViewPhotosHandler) {
            this.$root.$off('getViewPhotos', this.getViewPhotosHandler)
        }
        this.humanRequestId++
    },
    methods: {
        fetchHumans () {
            const eventId = this.eventId()
            const requestId = ++this.humanRequestId
            const isCurrentRequest = () => {
                return requestId === this.humanRequestId && String(eventId) === String(this.eventId())
            }

            if (!eventId) {
                this.humans = []
                this.loading = false
                return
            }
            this.loading = true
            this.axios.get(`${process.env.API_URL}/photographer/humans?eventId=${eventId}`).then((res) => {
                if (!isCurrentRequest()) return
                this.humans = res.data.humans || []
            }).catch(() => {
                if (!isCurrentRequest()) return
                this.$root.$emit('notification', 'is-danger', '人物一覧の取得に失敗しました')
            }).finally(() => {
                if (!isCurrentRequest()) return
                this.loading = false
            })
        },
        eventId () {
            return this.$parent.$parent.selectEventId
        },
        thumbStyle (human) {
            if (!human.representative) return {}
            const rep = human.representative
            const key = rep.s3Key || rep.reductionS3Key
            const url = `${this.photoBaseUrl}/${key}`

            // bounding box は元画像に対する 0-1 の比率。
            // background-size を `(1/width) * 100%` に拡大し、位置で顔を中央寄せにする。
            const bgW = (1 / Math.max(rep.width, 0.001)) * 100
            const bgH = (1 / Math.max(rep.height, 0.001)) * 100
            const posX = rep.width >= 1 ? 0 : (rep.left / (1 - rep.width)) * 100
            const posY = rep.height >= 1 ? 0 : (rep.top / (1 - rep.height)) * 100

            return {
                backgroundImage: `url('${url}')`,
                backgroundSize: `${bgW}% ${bgH}%`,
                backgroundPosition: `${posX}% ${posY}%`,
                backgroundRepeat: 'no-repeat'
            }
        },
        onNameEnter (e, human) {
            if (e.isComposing || e.keyCode === 229) return
            this.saveName(human)
        },
        saveName (human) {
            this.axios.put(`${process.env.API_URL}/photographer/human`, {
                id: human.id,
                name: human.name || ''
            }).then(() => {
                this.$root.$emit('notification', 'is-primary', '名前を保存しました')
            }).catch(() => {
                this.$root.$emit('notification', 'is-danger', '名前の保存に失敗しました')
            })
        },
        openHuman (human) {
            this.selectedHuman = human
            this.humanPhotos = []
            this.loadingPhotos = true
            const requestedId = human.id
            window.scrollTo(0, 0)
            this.axios.get(`${process.env.API_URL}/photographer/human/${human.id}/photos`).then((res) => {
                if (!this.selectedHuman || this.selectedHuman.id !== requestedId) return
                this.humanPhotos = res.data.photos || []
            }).catch(() => {
                if (!this.selectedHuman || this.selectedHuman.id !== requestedId) return
                this.$root.$emit('notification', 'is-danger', '写真の取得に失敗しました')
            }).finally(() => {
                if (this.selectedHuman && this.selectedHuman.id === requestedId) {
                    this.loadingPhotos = false
                }
            })
        },
        downloadCsv () {
            const eventId = this.eventId()
            if (!eventId) return
            this.downloadingCsv = true
            this.axios.get(`${process.env.API_URL}/photographer/humans/csv?eventId=${eventId}`, {
                responseType: 'blob'
            }).then((res) => {
                const disposition = res.headers['content-disposition'] || ''
                const match = disposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i)
                let filename = 'humans.csv'
                if (match && match[1]) {
                    try {
                        filename = decodeURIComponent(match[1])
                    } catch (e) {
                        filename = match[1]
                    }
                }
                fileDownload(res.data, filename)
            }).catch(() => {
                this.$root.$emit('notification', 'is-danger', 'CSV のダウンロードに失敗しました')
            }).finally(() => {
                this.downloadingCsv = false
            })
        },
        back () {
            this.selectedHuman = null
            this.humanPhotos = []
        }
    }
}
</script>

<style scoped>
.humans-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
    margin: 10px 0 18px;
}

.humans-header .intro-text {
    flex: 1;
    min-width: 240px;
    margin: 0;
}

.intro-text {
    margin: 10px 0 18px;
}

.loading-text {
    margin-top: 18px;
    text-align: center;
    color: rgb(90, 90, 90);
}

.humans {
    margin-top: 18px;
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 16px;
}

.human-card {
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 6px;
    padding: 10px;
    background: #fff;
    display: flex;
    flex-direction: column;
    align-items: stretch;
}

.face-thumb {
    position: relative;
    width: 100%;
    aspect-ratio: 1 / 1;
    background-color: #f0f0f0;
    border: 1px solid rgba(0, 0, 0, 0.1);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
}

.human-id {
    position: absolute;
    top: 4px;
    left: 4px;
    background: rgba(0, 0, 0, 0.55);
    color: #fff;
    font-size: 10px;
    line-height: 1;
    padding: 2px 5px;
    border-radius: 3px;
}

.no-face {
    color: #888;
    font-size: 12px;
}

.face-count {
    margin-top: 8px;
    font-size: 13px;
    color: #555;
    text-align: center;
}

.name-row {
    margin-top: 8px;
    display: flex;
    gap: 6px;
}

.name-row .input {
    flex: 1;
}

.detail-header {
    display: flex;
    align-items: center;
    gap: 16px;
    margin: 10px 0 18px;
}

.detail-title {
    margin: 0;
}

.photos {
    margin-top: 16px;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 14px;
}

.photo-wrap {
    width: 100%;
    border: 1px solid rgba(0, 0, 0, 0.1);
}

.photo-wrap > img {
    width: 100%;
    display: block;
}

.photo-name {
    border-top: 1px solid rgba(0, 0, 0, 0.1);
    padding: 3px 7px;
}
</style>



// WEBPACK FOOTER //
// src/components/photographer/pages/Humans.vue